import type { DbExecutor } from "./executor";
import { PlanRepository } from "./repositories/planRepository";
import { ObjectDefinitionRepository } from "./repositories/objectDefinitionRepository";
import {
  archiveOpening,
  detectConflicts,
  type ConflictObjectInfo,
  type DesignConflict,
  type PlanModel,
} from "../geometry";
import { elevationBand } from "../domain/objectConfig";
import type { ObjectDefinition } from "../domain/objectDefinition";
import type { ScopeSession } from "../design/scopeSession";

/**
 * CONFLICTS, AND WHAT THE USER DECIDED ABOUT THEM (spec §61C).
 *
 * The engine decides whether two things actually clash; this service gives
 * it the facts it cannot know — what each object IS and how far off the
 * floor it sits — and remembers the one decision worth remembering: "I know,
 * leave it alone."
 *
 * Resolving a conflict the other way ("remove the window in this plan")
 * stores nothing here at all: it is an ordinary Proposed decision, which
 * means it shows up in Proposed changes and can be cancelled there like any
 * other (spec §61B).
 */
export interface ReviewedConflict extends DesignConflict {
  /** True once the user has said they know about it. */
  accepted: boolean;
}

export class ConflictService {
  private readonly plans: PlanRepository;
  private readonly objectDefs: ObjectDefinitionRepository;

  constructor(private readonly db: DbExecutor) {
    this.plans = new PlanRepository(db);
    this.objectDefs = new ObjectDefinitionRepository(db);
  }

  /** What each object is and where it sits vertically. */
  private objectInfo(model: PlanModel, definitions: readonly ObjectDefinition[]): Map<string, ConflictObjectInfo> {
    const byId = new Map(definitions.map((d) => [d.id, d]));
    const info = new Map<string, ConflictObjectInfo>();
    for (const object of model.objects.values()) {
      if (object.archivedAt !== null) continue;
      const definition = byId.get(object.definitionId);
      if (!definition) continue;
      const band = elevationBand({
        mountHeightInches: object.mountHeightInches,
        heightInches: object.heightInches,
        definitionMountHeightInches: definition.defaultMountHeightInches,
        definitionHeightInches: definition.defaultHeightInches,
      });
      info.set(object.id, {
        label: definition.displayName,
        klass:
          definition.costBehavior === "visual"
            ? "ignore"
            : definition.category === "cabinet"
              ? "cabinet"
              : definition.category === "appliance"
                ? "appliance"
                : definition.category === "structure"
                  ? "structure"
                  : definition.category === "fixture"
                    ? "fixture"
                    : "ignore",
        bottomInches: band.bottomInches,
        topInches: band.topInches,
        inherited: object.originId !== null,
      });
    }
    return info;
  }

  /** Every clash in this plan, with what the user already said about it. */
  async list(designId: string, floorId: string, model?: PlanModel): Promise<ReviewedConflict[]> {
    const kind = await this.db.select<{ kind: string }>("SELECT kind FROM designs WHERE id = ?", [designId]);
    // Existing documents what is there — including awkward reality. Only a
    // plan can be in conflict with itself.
    if (kind[0]?.kind !== "proposed") return [];
    const plan = model ?? (await this.plans.loadFloor(designId, floorId));
    const definitions = await this.objectDefs.listAll();
    const addedWallIds = new Set(
      [...plan.walls.values()].filter((w) => w.archivedAt === null && w.originId === null).map((w) => w.id),
    );
    const conflicts = detectConflicts({ model: plan, info: this.objectInfo(plan, definitions), addedWallIds });
    const decided = await this.db.select<{ conflict_key: string }>(
      "SELECT conflict_key FROM design_conflict_dispositions WHERE design_id = ?",
      [designId],
    );
    const accepted = new Set(decided.map((d) => d.conflict_key));
    return conflicts.map((c) => ({ ...c, accepted: accepted.has(c.key) }));
  }

  /** "I know — leave the design as it is." */
  async accept(designId: string, conflictKey: string): Promise<void> {
    await this.db.execute(
      `INSERT OR REPLACE INTO design_conflict_dispositions (design_id, conflict_key, disposition, decided_at)
       VALUES (?, ?, 'accepted', ?)`,
      [designId, conflictKey, new Date().toISOString()],
    );
  }

  async reopen(designId: string, conflictKey: string): Promise<void> {
    await this.db.execute("DELETE FROM design_conflict_dispositions WHERE design_id = ? AND conflict_key = ?", [
      designId,
      conflictKey,
    ]);
  }

  /**
   * Resolve it by taking the opening out of THIS PLAN — an intentional
   * Proposed removal, exactly as if the user had deleted it on the canvas.
   * Existing keeps its window: the house still has one, the plan does not.
   */
  async removeOpeningInPlan(session: ScopeSession, conflict: DesignConflict): Promise<void> {
    if (!conflict.openingId) throw new Error("There is no opening to remove for that conflict.");
    const openingId = conflict.openingId;
    session.commit("Remove opening (conflict)", (model, ctx) => archiveOpening(model, ctx, openingId));
    await session.flush();
  }
}
